/**
 * Google's own "Sign in with Google" button (Google Identity Services). GIS
 * draws the button in an iframe and hands back an ID token; the backend
 * verifies it (`POST /api/login/google`). The script is loaded on demand, so a
 * server without Google sign-in never fetches it.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** The slice of `google.accounts.id` this button uses. */
interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      width?: number;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let gisLoading: Promise<GoogleAccountsId> | undefined;

function loadGis(): Promise<GoogleAccountsId> {
  gisLoading ??= new Promise<GoogleAccountsId>((resolve, reject) => {
    const ready = (): void => {
      const id = window.google?.accounts?.id;
      if (id) resolve(id);
      else reject(new Error('Google sign-in did not load.'));
    };
    if (window.google?.accounts?.id) {
      ready();
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = ready;
    script.onerror = () => reject(new Error('Could not reach Google sign-in.'));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    // Let a later mount retry (e.g. after the network comes back).
    gisLoading = undefined;
    throw err;
  });
  return gisLoading;
}

export function GoogleSignInButton({
  clientId,
  onCredential,
  onError,
  disabled = false,
  text = 'continue_with',
}: {
  clientId: string;
  /** Called with the Google ID token once the user picks an account. */
  onCredential: (idToken: string) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  /** Google's button caption, e.g. `signup_with` on the sign-up page. */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
}): ReactNode {
  const host = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  // GIS keeps the callback from initialize(); route it through a ref so it
  // always reaches the latest props without re-rendering Google's button.
  const handlers = useRef({ onCredential, onError });
  useEffect(() => {
    handlers.current = { onCredential, onError };
  });

  useEffect(() => {
    let active = true;
    loadGis().then(
      (gis) => {
        if (!active || !host.current) return;
        gis.initialize({
          client_id: clientId,
          ux_mode: 'popup',
          auto_select: false,
          callback: ({ credential }) => {
            if (credential) handlers.current.onCredential(credential);
            else handlers.current.onError('Google did not return a sign-in.');
          },
        });
        host.current.replaceChildren();
        gis.renderButton(host.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'center',
          width: Math.min(400, Math.max(200, host.current.clientWidth)),
        });
        setLoaded(true);
      },
      (err: unknown) => {
        if (active) handlers.current.onError(err instanceof Error ? err.message : 'Google sign-in is unavailable.');
      },
    );
    return () => {
      active = false;
    };
  }, [clientId, text]);

  return (
    <div
      ref={host}
      aria-busy={!loaded}
      style={{
        minHeight: 40,
        display: 'flex',
        justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : undefined,
      }}
    />
  );
}
